import { useAppFilters, useQueryNavigation } from '@/hooks/shared';
import { ASSETS_TYPE } from '@/types/shared/navigation-tabs.enums';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { format, parse } from 'date-fns';
import { getFormattedDuration } from '@/components/pages/AssetsStudioPage/MainContent/Alarms/utils';

export interface SensorData {
  data_source?: string;
  last_uptime: string;
  sensor_name: string;
  sensor_type: string;
  health_status: string;
  located_at: string;
  component_id: string;
}

const healthTypeConvert = {
  online: 'text-healthy',
  offline: 'text-critical',
};

const tdClasses = 'flex !justify-start pl-6 capitalize pr-6';

export const useAlarmsTable = ({ additionalHeaders, dataType }: { additionalHeaders: any; dataType: any }) => {
  const { assetsType } = useAppFilters();
  const { currentParams } = useQueryNavigation();
  const sites_table_structure: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: 'component_name',
        header: <div className="">Component Name</div>,
        cell: (info: any) => (
          <div className={cn('capitalized font-semibold text-primary-500 cursor-pointer', tdClasses)}>
            {info.getValue()}
          </div>
        ),
        enableFiltering: true,
      },
      {
        accessorKey: 'alarm_name',
        header: <div className="">Alarm Name</div>,
        cell: (info: any) => <div className={cn('capitalized', tdClasses)}>{info.getValue()}</div>,
        enableFiltering: false,
        enableSearch: true,
      },
      {
        accessorKey: 'oem_id',
        header: <div className="border-2">OEM ID</div>,
        cell: (info: any) => <div className={tdClasses}>{info.getValue()}</div>,
        enableFiltering: true,
        enableSearch: true,
      },
      {
        accessorKey: 'priority',
        header: <div className="">Priority</div>,
        cell: (info: any) => <div className={tdClasses}>{info.getValue()}</div>,
        enableFiltering: false,
      },
      {
        accessorKey: 'alarm_severity',
        header: <div className="">Severity</div>,
        cell: (info: any) => (
          <div className={cn('capitalized', tdClasses)}>{info.getValue()?.replaceAll('_', ' ')}</div>
        ),
        enableFiltering: false,
      },
      ...additionalHeaders.map((date: string, index: number) => ({
        accessorKey: date,
        header: (
          <div className="flex items-center justify-center w-full ">
            {format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM dd')}
          </div>
        ),
        cell: (info: any) => {
          const value = info.row.original[date];
          return (
            <div
              className={cn(
                'flex justify-center w-full items-center h-16 bg-gray-50',
                index === 0
                  ? 'border-l-2 border-gray-300'
                  : index === additionalHeaders.length - 1
                  ? 'border-r-2 border-gray-300'
                  : ''
              )}
            >
              {value && dataType === 'duration' ? getFormattedDuration(value) : value ?? '-'}
            </div>
          );
        },
        sortingFn: (rowA: any, rowB: any) => {
          const a = rowA.original[date] ?? 0;
          const b = rowB.original[date] ?? 0;
          return a - b;
        },
        enableSorting: true,
        enableFiltering: false,
      })),
      {
        accessorKey: 'total',
        header: <div className="border-2 min-w-full">Total</div>,
        cell: (info: any) => (
          <div className="bg-gray-100 !min-w-full !flex !flex-1">
            {dataType === 'duration' ? getFormattedDuration(info.getValue()) : info.getValue()}
          </div>
        ),
      },

      // {
      //   accessorKey: 'description',
      //   header: <div className="">description</div>,
      //   cell: (info: any) => <div className={tdClasses}>{info.getValue()}</div>,
      //   enableFiltering: false,
      // },
      // {
      //   accessorKey: 'interface',
      //   header: <div className="">Interface</div>,
      //   cell: (info: any) => <div className={tdClasses}>{info.getValue()}</div>,
      //   enableFiltering: true,
      // },
      {
        accessorKey: 'empty',
        header: <div className=""></div>,
        cell: (info: any) => <div className="capitalized">{info.getValue()}</div>,
        enableFiltering: false,
      },
    ],
    // .filter((el: any) => {
    //   if (groupBy === 'by_alarm') return true;
    //   else return !blackList.includes(el.accessorKey);
    // }
    // )
    [additionalHeaders, currentParams, dataType]
  );
  return {
    tableStructure: sites_table_structure.filter((el: any) =>
      assetsType === ASSETS_TYPE.FIXED ? el.accessorKey !== 'data_source' : true
    ),
  };
};
